import { EFinancingProgram, EProvince } from '@if/core/types'
import {
  BaseWorksheetFormConfig,
  buildDefaultSubmitValidators,
  runSubmitValidations,
  WorksheetError,
} from '@src/components/WorksheetForm'
import { useComputePersonalLoanFunding } from '@src/data/api/personal-loan-worksheet/personal-loan-worksheet-api'
import { useProvinceConfigurations } from '@src/data/selectors/app-config-selectors'
import { FundingComputedDto } from '@src/data/types/FundingComputedDto'
import { FundingInfoDto } from '@src/data/types/FundingInfoDto'
import { nanoid } from 'nanoid'
import { useCallback, useEffect, useRef, useState } from 'react'
import { CalculatorValues } from '../calculatorSchema'

export interface UseCalculatorComputeArgs {
  // Nullable so callers can wire the hook at the page level before all
  // dependencies are ready. While any required field is missing `compute`
  // resolves without doing anything; it becomes active once everything's set.
  config: BaseWorksheetFormConfig | null
  financingProgramId: EFinancingProgram | null
  hasCoApplicant: boolean
  stateIso: EProvince | null
  creditApplicationId: string | null
  merchantId?: string
  interestRate: number
  insuranceCompanyId: string | null
}

export interface CalculatorComputeResult {
  info: FundingComputedDto
  values: CalculatorValues
}

export interface UseCalculatorComputeReturn {
  compute: (values: CalculatorValues) => Promise<void>
  result: CalculatorComputeResult | null
  submitErrors: WorksheetError[]
  isComputing: boolean
}

export const useCalculatorCompute = ({
  config,
  financingProgramId,
  hasCoApplicant,
  stateIso,
  creditApplicationId,
  merchantId,
  interestRate,
  insuranceCompanyId,
}: UseCalculatorComputeArgs): UseCalculatorComputeReturn => {
  const [computeFunding, isComputing] = useComputePersonalLoanFunding()
  const provinceConfigs = useProvinceConfigurations()
  const [result, setResult] = useState<CalculatorComputeResult | null>(null)
  const [submitErrors, setSubmitErrors] = useState<WorksheetError[]>([])

  // Aborts the previous compute when called again before the first run finishes
  // — keeps stale validator results from clobbering fresh ones.
  const abortRef = useRef<AbortController | null>(null)
  useEffect(() => () => abortRef.current?.abort(), [])

  const compute = useCallback(
    async (data: CalculatorValues) => {
      if (!config || !financingProgramId || !stateIso) return
      if (!data.firstPaymentDate || data.term === null) return

      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      const dto: FundingInfoDto = {
        financingProgramId,
        amountRequested: data.amountRequested,
        includeInsurance: data.includeProtection,
        insuranceCompanyId,
        paymentPlanId: data.paymentPlanId,
        paymentFrequency: data.paymentFrequency,
        term: data.term,
        interestRate,
        hasCoApplicant,
        deliveryOn: data.activationDate,
        firstPaymentOn: data.firstPaymentDate.toString(),
        requestId: nanoid(),
        stateIso,
        creditApplicationId: creditApplicationId ?? '',
        merchantId: merchantId ?? null,
      }

      try {
        const info = await computeFunding(dto)
        if (controller.signal.aborted) return

        const validators = buildDefaultSubmitValidators({
          config,
          computedInfo: info,
          provinceConfigs,
          paymentPlanId: data.paymentPlanId,
          amountRequested: data.amountRequested,
          activationDate: data.activationDate,
        })
        const validation = await runSubmitValidations(validators, controller.signal)
        setSubmitErrors(validation.ok ? [] : validation.errors)
        setResult({ info, values: data })
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return
        setResult(null)
        setSubmitErrors([])
      }
    },
    [
      config,
      financingProgramId,
      hasCoApplicant,
      stateIso,
      creditApplicationId,
      merchantId,
      interestRate,
      insuranceCompanyId,
      computeFunding,
      provinceConfigs,
    ],
  )

  return { compute, result, submitErrors, isComputing }
}
